import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useShowTileForm } from '../../lib/redux/hooks/useShowTileForm';
import useHandleTiles from '../../lib/redux/hooks/useHandleTiles';
import { useTileView } from '../../lib/redux/hooks/useTileView';
import Text from '../Text';

const UserSubmittedMenu = ({ tileId, currentTab, creatorName }) => {
  const { acceptTile, ignoreTile } = useHandleTiles();
  const { t } = useTranslation('tileView');

  return (
    <Menu id="test_user_submitted_menu">
      <li className="test_creator_item">
        <Text color="primary.100" variant="button">
          {t('menu.submittedBy', { creator: creatorName })}
        </Text>
      </li>
      <li className="test_accept_item">
        <MenuText
          onClick={() => acceptTile({ id: tileId, status: currentTab })}
          color="primary.100"
          variant="button"
        >
          {t('menu.accept')}
        </MenuText>
      </li>
      <li className="test_ignore_item">
        <MenuText
          onClick={() => ignoreTile({ id: tileId, status: currentTab })}
          color="primary.100"
          variant="button"
        >
          {t('menu.ignore')}
        </MenuText>
      </li>
    </Menu>
  );
};

const DefaultMenu = ({ tileId, editPath, currentTab, refetch, planDate }) => {
  const { copyTile, deleteTile, editTile: oldEditTile } = useHandleTiles();
  const { hideTileView } = useTileView();
  const { t } = useTranslation('tileView');
  const [editing, setEditing] = useState(false);
  const { editTile } = useShowTileForm();
  const { newTileForm } = useSelector(state => state.userData);

  useEffect(() => {
    if (editing) {
      const editCloseButton = document.querySelector(
        '.close-reveal-modal.stickable',
      );

      const editButtonClick = () => {
        refetch();
        setEditing(false);
      };

      editCloseButton.addEventListener('click', editButtonClick);
    }
  }, [refetch, editing]);

  return (
    <Menu>
      <li>
        <Text variant="button">{t(`menu.${currentTab}`)}</Text>
      </li>
      <li>
        <MenuText
          onClick={() => {
            if (newTileForm) {
              editTile({ tileId });
            } else {
              setEditing(true);
              oldEditTile({
                id: tileId,
                status: currentTab,
                editPath,
                planDate,
              });
            }
          }}
          color="primary.100"
          variant="button"
        >
          {t('menu.edit')}
        </MenuText>
      </li>
      <li>
        <MenuText
          onClick={() => deleteTile({ id: tileId, status: currentTab })}
          color="primary.100"
          variant="button"
        >
          {t('menu.delete')}
        </MenuText>
      </li>
      <li>
        <MenuText
          onClick={() => copyTile({ id: tileId, status: currentTab })}
          color="primary.100"
          variant="button"
        >
          {t('menu.copy')}
        </MenuText>
      </li>
      <CloseIcon onClick={hideTileView} />
    </Menu>
  );
};

const TileViewMenu = ({
  currentTab,
  tileId,
  editPath,
  refetch,
  creatorName,
  planDate,
}) => {
  switch (currentTab) {
    case 'user_submitted':
      return (
        <UserSubmittedMenu
          currentTab={currentTab}
          tileId={tileId}
          creatorName={creatorName}
        />
      );
    case 'plan':
    case 'draft':
    case 'active':
    case 'archive':
    default:
      return (
        <DefaultMenu
          currentTab={currentTab}
          tileId={tileId}
          editPath={editPath}
          refetch={refetch}
          planDate={planDate}
        />
      );
  }
};

export default TileViewMenu;

const Menu = styled.ul`
  width: 100%;
  display: flex;
  background: white;
  border-radius: 8px;
  border: 1px solid ${themeGet('colors.primary.100')};
  margin: auto;
  padding: 11px 0;
  justify-content: space-around;
  width: 100%;
  max-width: 600px;
  align-items: center;
  position: relative;
`;

const MenuText = styled(Text)`
  cursor: pointer;
`;

const CloseIcon = styled.i`
  cursor: pointer;
  position: absolute;
  left: 105%;
`;

CloseIcon.defaultProps = {
  className: 'fa-regular fa-xmark'
};
